import {noop} from '@github-ui/noop'
import {useSyncedState} from '@github-ui/use-synced-state'
import {useResizeObserver} from '@primer/react'
import {
  createContext,
  type PropsWithChildren,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {useActionBarRef} from './ActionBarRefContext'
import {batchGetItemWidths, getItemWidth} from './utils'

type ActionBarResizeContextProps = {
  /**
   * The index of the key for the last `action` node that fits on the screen.
   */
  visibleChildEndIndex: number
  /**
   * Whether the justify-content CSS style for the outer container should be 'space-between' or not.
   */
  justifySpaceBetween: boolean

  /**
   * Handler to trigger a recalculation of which action items should be visible versus hidden in the overflow menu.
   * Can be used in one of the given nested menus or nested select panels, such as if the selection of list items
   * changes in the select panel and that affects the width of the select panel toggle button rendered in the action
   * bar.
   */
  recalculateItemSize: (key: string, el: HTMLElement) => void
  /**
   * Whether the initial measurement and visibility calculation has completed.
   * While false, the container should be hidden via CSS to prevent layout flicker.
   */
  ready: boolean
}

const ActionBarResizeContext = createContext<ActionBarResizeContextProps>({
  visibleChildEndIndex: 0,
  justifySpaceBetween: false,
  recalculateItemSize: noop,
  ready: true,
})

export type ActionBarResizeProviderValueProps = {
  actionKeys: string[]
}

type ActionBarResizeProviderProps = PropsWithChildren & {value: ActionBarResizeProviderValueProps}

export const ActionBarResizeProvider = ({children, value: {actionKeys}}: ActionBarResizeProviderProps) => {
  const totalActions = actionKeys.length
  const [visibleChildEndIndex, setVisibleChildEndIndex] = useSyncedState(totalActions)
  const {outerContainerRef, itemContainerRef} = useActionBarRef()
  const initialBarWidthRef = useRef<number | undefined>(undefined)
  const [itemOffsetWidths, setItemOffsetWidths] = useState<Map<string, number | undefined>>(
    () => new Map(actionKeys.map(key => [key, undefined])),
  )
  const [justifySpaceBetween, setJustifySpaceBetween] = useState<boolean>(false)
  const [ready, setReady] = useState(false)
  // Tracks which items were measured in the current batch cycle so that
  // per-item VisibleItem layout effects can skip redundant measurements.
  // .current is reassigned each cycle so a cleanup would clear the wrong Set instance (it only holds string keys).
  // eslint-disable-next-line @github-ui/web-vitals/no-element-collection-ref-without-cleanup
  const batchMeasuredKeysRef = useRef<Set<string>>(new Set())

  const recalculateItemSize = useCallback(
    (key: string, el: HTMLElement) => {
      // Skip if this item was already measured in the current batch cycle
      if (batchMeasuredKeysRef.current.has(key)) {
        batchMeasuredKeysRef.current.delete(key)
        return
      }

      // If the item container isn't shown, none of the items are visible so we can't know their size
      const itemContainer = itemContainerRef.current
      if (!itemContainer) return

      const newWidth = getItemWidth(el, itemContainer)

      setItemOffsetWidths(oldVal => {
        if (newWidth === oldVal.get(key) || isNaN(newWidth)) return oldVal

        const newVal = new Map<string, number | undefined>(Array.from(oldVal.entries()))
        newVal.set(key, newWidth)
        return newVal
      })
    },
    [itemContainerRef],
  )

  // Calculate the final visibleChildEndIndex in a single update cycle.
  // Uses the same shrink/grow semantics as the original but loops to find the
  // final index rather than moving one item per render cycle.
  const update = useCallback(() => {
    const outerContainer = outerContainerRef.current
    const itemContainer = itemContainerRef.current
    if (!outerContainer || !itemContainer) return

    const outerWidth = outerContainer.offsetWidth
    if (!outerWidth) return

    // Read the current layout state once
    const itemContainerWidth = itemContainer.offsetWidth
    const itemContainerLeft = itemContainer.offsetLeft

    // availableSpace: how much space is left in the outer container beyond the items
    let availableSpace = outerWidth - itemContainerWidth
    // menuSpace: the portion of availableSpace occupied by the overflow menu button
    const menuSpace = availableSpace - itemContainerLeft

    let index = visibleChildEndIndex

    // Shrink: remove items from the right while there isn't enough space
    while (index > 0 && availableSpace <= menuSpace) {
      const key = actionKeys[index - 1]
      if (!key) break
      const w = itemOffsetWidths.get(key)
      if (typeof w === 'undefined') break
      index--
      // When we remove an item, available space increases by that item's width
      availableSpace += w
    }

    // Grow: add items from the left while there's enough space for the next item + menu
    while (index < totalActions) {
      const key = actionKeys[index]
      if (!key) break
      const w = itemOffsetWidths.get(key)
      if (typeof w === 'undefined') break
      if (availableSpace <= menuSpace + w) break
      index++
      // When we add an item, available space decreases by that item's width
      availableSpace -= w
    }

    setVisibleChildEndIndex(index)

    if (initialBarWidthRef.current === undefined) {
      initialBarWidthRef.current = itemContainerWidth
    }
    setJustifySpaceBetween(outerWidth <= (initialBarWidthRef.current ?? itemContainerWidth))
    setReady(true)
  }, [
    actionKeys,
    itemOffsetWidths,
    outerContainerRef,
    itemContainerRef,
    totalActions,
    visibleChildEndIndex,
    setVisibleChildEndIndex,
  ])

  // Batch-measure all item widths and run the shrink/grow update.
  // On initial mount we use useEffect so the measurement happens after paint,
  // avoiding a ~31ms forced reflow that blocks first paint.
  const hasMountedRef = useRef(false)

  const measureAndUpdate = useCallback(() => {
    const itemContainer = itemContainerRef.current
    if (!itemContainer) return

    const items: Array<{key: string; el: HTMLElement}> = []
    for (const key of actionKeys) {
      const el = itemContainer.querySelector<HTMLElement>(`[data-action-bar-item="${key}"]`)
      if (el) items.push({key, el})
    }

    const widths = batchGetItemWidths(items, itemContainer)
    batchMeasuredKeysRef.current = new Set(widths.keys())
    setItemOffsetWidths(widths)
  }, [actionKeys, itemContainerRef])

  // Initial mount: defer measurement to after paint
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      measureAndUpdate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Subsequent actionKeys changes: measure synchronously to prevent layout flash
  useLayoutEffect(() => {
    if (!hasMountedRef.current) return
    measureAndUpdate()
  }, [measureAndUpdate])

  useLayoutEffect(() => {
    if (!hasMountedRef.current) return
    update()
  }, [update])

  useResizeObserver(update, outerContainerRef)

  const value = useMemo<ActionBarResizeContextProps>(
    () => ({visibleChildEndIndex, justifySpaceBetween, recalculateItemSize, ready}),
    [visibleChildEndIndex, justifySpaceBetween, recalculateItemSize, ready],
  )
  return <ActionBarResizeContext value={value}>{children}</ActionBarResizeContext>
}

export const useActionBarResize = () => {
  return use(ActionBarResizeContext)
}
